import React, { useState } from "react";
import { useMutation } from "@apollo/client/react";
import {
  ReadEvidencingDocument,
  type FullResourceFragment,
  type TriviaResultFragment,
  type SubmitTriviaAnswerMutation,
  type SubmitTriviaAnswerMutationVariables,
} from "~/graphql/codegen/graphql";
import { SUBMIT_TRIVIA_ANSWER_MUTATION } from "./annotate-mutations";
import { Link, useLocation } from "react-router";
import { Button, Carousel, CarouselApi, CarouselContent, CarouselItem, Dialog, DialogContent } from "@mindstonehq/ui";
import { QuestionMCQ } from "../resource-trivia/question-mcq";
import { AnswerMCQ } from "../resource-trivia/answer-mcq";
import QuestionChoice from "../resource-trivia/question-choice";
import { Trans } from "@lingui/react/macro";

export enum TRIVIA_STEPS {
  QUESTION_CHOICE,
  QUESTION_MCQ,
  ANSWER_MCQ,
  QUESTION_FREETEXT,
  ANSWER_FREETEXT,
}

const steps = [TRIVIA_STEPS.QUESTION_CHOICE, TRIVIA_STEPS.QUESTION_MCQ, TRIVIA_STEPS.ANSWER_MCQ];
export default function AnnotateTriviaBar({ resource }: { resource: FullResourceFragment }) {
  const [submitQuestion, { loading }] = useMutation<SubmitTriviaAnswerMutation, SubmitTriviaAnswerMutationVariables>(
    SUBMIT_TRIVIA_ANSWER_MUTATION,
  );
  const location = useLocation();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [triviaVisible, setTriviaVisible] = useState(false);
  const [mcqAnswer, setMCQAnswer] = useState("");
  const [carousel, setCarousel] = React.useState<CarouselApi>();
  const [triviaResult, setTriviaResult] = useState<TriviaResultFragment>();
  const [initialAnswerOrder, setInitialAnswerOrder] = React.useState<string[]>([]);

  const moveSlide = (direction: "forward" | "back") => {
    if (carousel) {
      if (direction === "forward") {
        carousel.canScrollNext() && carousel.scrollNext();
      } else {
        carousel.canScrollPrev() && carousel.scrollPrev();
      }
    }
  };

  const triviaSubmit = async () => {
    const result = await submitQuestion({
      variables: {
        articleID: resource.id,
        questionID: resource.trivia?.questions[0].id || "",
        answerID: mcqAnswer,
      },
      refetchQueries: [ReadEvidencingDocument],
    });
    setTriviaResult(result.data?.submitTriviaAnswer);
    moveSlide("forward");
  };

  const getComponent = (step: TRIVIA_STEPS) => {
    switch (step) {
      case TRIVIA_STEPS.QUESTION_CHOICE:
        return (
          <QuestionChoice
            choices={[TRIVIA_STEPS.QUESTION_MCQ]}
            earnablePoints={{
              mcq: resource.evidencing.totalEarnablePoints,
            }}
            skippable={true}
            nextStep={() => moveSlide("forward")}
          />
        );
      case TRIVIA_STEPS.QUESTION_MCQ:
        return (
          <QuestionMCQ
            triviaSelected={(a) => setMCQAnswer(a)}
            selectedAns={mcqAnswer}
            question={resource.trivia?.questions[0]}
            onInitialOrderSet={setInitialAnswerOrder}
          >
            <div className="w-full flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-4 z-10">
              <Button
                variant="secondary"
                size="lg"
                className="w-full md:w-auto md:min-w-[132px]"
                onClick={triviaSubmit}
                disabled={!mcqAnswer || loading}
              >
                {loading ? <Trans>Submitting...</Trans> : <Trans>Submit</Trans>}
              </Button>
            </div>
          </QuestionMCQ>
        );
      case TRIVIA_STEPS.ANSWER_MCQ:
        return (
          <AnswerMCQ trivia={resource.trivia} result={triviaResult} initialAnswerOrder={initialAnswerOrder}>
            <div className="flex items-center justify-end gap-4 pt-2">
              <Link
                to={location.state?.back?.pathname.includes(".mindstone") ? location.state?.back?.pathname : "/"}
                state={{ back: { ...location } }}
              >
                <Button variant="outline" className="w-full">
                  <Trans>All Done</Trans>
                </Button>
              </Link>
            </div>
          </AnswerMCQ>
        );
      default:
        return <div></div>;
    }
  };
  return (
    <>
      {resource.following && (
        <div className="h-16 p-4 bg-quaternary rounded-sm justify-between items-center gap-4 flex">
          <div><Trans>Finished reading?</Trans></div>
          <Button size="sm" variant="secondary" onClick={() => setTriviaVisible(true)} id="mark_as_done_btn">
            <Trans>Start Trivia</Trans>
          </Button>
        </div>
      )}
      {triviaVisible && (
        <Dialog
          open={triviaVisible}
          onOpenChange={(open) => {
            if (open) {
              return;
            }
            setTriviaVisible(false);
          }}
        >
          <DialogContent
            ref={containerRef}
            className="h-[100dvh] sm:h-auto max-h-[100dvh] md:max-w-[80%] w-full md:w-auto sm:min-w-[560px] overflow-auto py-8"
          >
            {triviaVisible && resource && (
              <Carousel
                opts={{
                  watchDrag: false,
                }}
                disableKeyNavigation={true}
                setApi={setCarousel}
                className="w-full"
              >
                <CarouselContent>
                  {steps.map((step, index) => {
                    const component = getComponent(step);
                    return <CarouselItem key={index}>{component}</CarouselItem>;
                  })}
                </CarouselContent>
              </Carousel>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
